import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Eye, Download, Share2, Copy, Check, X, 
  Loader2, Mail, User, AlertTriangle, ChevronRight,
  Image as ImageIcon, Globe
} from 'lucide-react';
import { CountryPhoneInput, COUNTRIES } from '../components/CountryPhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function LeadCaptureModal({ isOpen, onClose, strategy, onSuccess }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [countryCode, setCountryCode] = useState('IN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-detect country on mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Try to detect from browser timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneToCountry = {
          'Asia/Kolkata': 'IN',
          'Asia/Calcutta': 'IN',
          'America/New_York': 'US',
          'America/Los_Angeles': 'US',
          'America/Chicago': 'US',
          'Europe/London': 'GB',
          'Asia/Dubai': 'AE',
          'Asia/Singapore': 'SG',
          'Australia/Sydney': 'AU',
          'Australia/Melbourne': 'AU',
          'America/Toronto': 'CA',
        };
        
        if (timezoneToCountry[timezone]) {
          setCountryCode(timezoneToCountry[timezone]);
          return;
        }
        
        // Fallback: try IP-based detection (free service)
        const res = await fetch('https://ipapi.co/json/', { timeout: 3000 });
        if (res.ok) {
          const data = await res.json();
          const detectedCode = data.country_code?.toUpperCase();
          if (COUNTRIES.find(c => c.code === detectedCode)) {
            setCountryCode(detectedCode);
          }
        }
      } catch (e) {
        // Fallback to India if detection fails
        console.log('Country detection failed, defaulting to IN');
        setCountryCode('IN');
      }
    };
    
    if (isOpen) {
      detectCountry();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    
    // Basic phone format validation
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 8) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: phoneDigits,
          country_code: countryCode,
          strategy_slug: strategy.slug
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        onSuccess(data.download_url);
      } else {
        setError(data.detail || 'Failed to process request');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-md bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 bg-gradient-to-r from-[#8B5CF6]/10 to-[#007AFF]/10">
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X size={16} className="text-white/60" />
          </button>
          <h2 className="font-heading font-bold text-xl text-white">
            Download {strategy.name}
          </h2>
          <p className="font-body text-sm text-white/50 mt-1">
            Enter your details to get instant access
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="font-body text-xs text-white/50 mb-1.5 block">Name *</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                placeholder="Your name"
                required
                data-testid="lead-name-input"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-xs text-white/50 mb-1.5 block">Email *</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                placeholder="your@email.com"
                required
                data-testid="lead-email-input"
              />
            </div>
          </div>

          <CountryPhoneInput
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            phone={formData.phone}
            setPhone={(value) => setFormData(prev => ({ ...prev, phone: value }))}
            label="Phone"
            testIdPrefix="lead"
          />

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-70"
            data-testid="lead-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Download size={18} />
                Get Download Link
              </>
            )}
          </button>

          <p className="text-[10px] text-white/30 text-center">
            We respect your privacy. Your information is secure with us.
          </p>
        </form>
      </motion.div>
    </div>
  );
}

function DownloadSuccessModal({ isOpen, onClose, downloadUrl }) {
  const handleDownload = () => {
    window.open(`${API_URL}${downloadUrl}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-sm bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl p-6 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-[#10B981]" />
        </div>
        <h3 className="font-heading font-bold text-lg text-white mb-2">Download Ready!</h3>
        <p className="text-white/50 text-sm mb-6">Click below to download your strategy file.</p>
        
        <button
          onClick={handleDownload}
          className="w-full py-3.5 bg-gradient-to-r from-[#10B981] to-[#059669] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2"
          data-testid="download-file-btn"
        >
          <Download size={18} />
          Download Now
        </button>
        
        <button
          onClick={onClose}
          className="w-full mt-3 py-2.5 text-white/50 hover:text-white text-sm transition-colors"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
}

export default function StrategyDetailPage() {
  const { slug } = useParams();
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStrategy();
  }, [slug]);

  const fetchStrategy = async () => {
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/strategies/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setStrategy(data);
      }
    } catch (err) {
      console.error('Failed to fetch strategy:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLeadSuccess = (url) => {
    setShowLeadModal(false);
    setDownloadUrl(url);
    setShowSuccessModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#8B5CF6] animate-spin" />
      </div>
    );
  }

  if (!strategy) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-2">Strategy Not Found</h2>
          <p className="text-white/50 mb-6">This strategy doesn&apos;t exist or has been removed.</p>
          <Link 
            to="/strategy-lab" 
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-white text-sm font-medium transition-colors"
          >
            <ArrowLeft size={16} /> Back to Strategy Lab
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Header */}
      <section className="pt-24 pb-8">
        <div className="max-w-4xl mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
            <Link to="/strategy-lab" className="hover:text-white transition-colors">Strategy Lab</Link>
            <ChevronRight size={14} />
            <span className="text-white/60">{strategy.name}</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left Column - Banner & Info */}
            <div className="lg:col-span-3 space-y-6">
              {/* Banner Image */}
              <div className="aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-[#8B5CF6]/20 to-[#007AFF]/20 border border-white/5">
                {strategy.banner_image ? (
                  <img
                    src={strategy.banner_image.startsWith('monkalgo/') 
                      ? `${API_URL}/api/strategy-lab/files/${strategy.banner_image}`
                      : `${API_URL}/api/strategy-lab/images/${strategy.banner_image}`}
                    alt={strategy.name}
                    className="w-full h-full object-cover"
                    key={strategy.banner_image}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-white/10" />
                  </div>
                )}
              </div>

              {/* Strategy Info */}
              <div>
                <h1 className="font-heading font-bold text-3xl text-white mb-3">{strategy.name}</h1>
                
                <div className="flex items-center gap-4 mb-6">
                  {strategy.instrument && (
                    <span className="px-3 py-1.5 rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-[#8B5CF6] text-sm font-medium">
                      {strategy.instrument}
                    </span>
                  )}
                  <div className="flex items-center gap-4 text-white/40 text-sm">
                    <span className="flex items-center gap-1.5">
                      <Eye size={14} /> {strategy.view_count || 0} views
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Download size={14} /> {strategy.download_count || 0} downloads
                    </span>
                  </div>
                </div>

                {strategy.description && (
                  <div className="glass rounded-xl p-5">
                    <h3 className="font-heading font-semibold text-white mb-2">About this Strategy</h3>
                    <p className="text-white/60 leading-relaxed whitespace-pre-line">
                      {strategy.description}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Download CTA */}
            <div className="lg:col-span-2">
              <div className="glass rounded-2xl p-6 sticky top-24">
                <h3 className="font-heading font-semibold text-lg text-white mb-2">Get This Strategy</h3>
                <p className="text-white/50 text-sm mb-6">
                  Download the Pine Script / EA file for free.
                </p>

                {strategy.script_file ? (
                  <button
                    onClick={() => setShowLeadModal(true)}
                    className="w-full py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                    data-testid="download-strategy-btn"
                  >
                    <Download size={18} />
                    Download Free
                  </button>
                ) : (
                  <div className="w-full py-3.5 bg-white/5 border border-white/10 rounded-xl text-white/40 font-heading text-sm flex items-center justify-center gap-2">
                    <AlertTriangle size={18} />
                    Coming Soon
                  </div>
                )}

                <button
                  onClick={handleShare}
                  className="w-full mt-3 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  {copied ? <Check size={16} className="text-[#10B981]" /> : <Share2 size={16} />}
                  {copied ? 'Link Copied!' : 'Share'}
                </button>

                {/* Disclaimer */}
                <p className="text-[10px] text-white/30 text-center mt-4 leading-relaxed">
                  By downloading, you agree that this is for educational purposes only. 
                  Trading involves risk of loss.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back Link */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link 
            to="/strategy-lab" 
            className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft size={16} /> Back to Strategy Lab
          </Link>
        </div>
      </section>

      {/* Lead Capture Modal */}
      <AnimatePresence>
        {showLeadModal && (
          <LeadCaptureModal
            isOpen={showLeadModal}
            onClose={() => setShowLeadModal(false)}
            strategy={strategy}
            onSuccess={handleLeadSuccess}
          />
        )}
      </AnimatePresence>

      {/* Download Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <DownloadSuccessModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
            downloadUrl={downloadUrl}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
